import { useState, useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { Link, useNavigate } from 'react-router-dom';
import { 
  QrCode, 
  BookOpen, 
  MousePointer, 
  TrendingUp,
  Plus,
  Activity,
  Calendar,
  Globe,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Megaphone,
  UserPlus
} from 'lucide-react';
import { usePlanLimits } from '../hooks/usePlanLimits';
import TrialActivation from '../components/TrialActivation';
import { TrialBanner } from '../components/TrialBanner';
import { useTrial } from '../hooks/useTrial';
import { BookCarousel } from '../components/dashboard/BookCarousel';
import { QRPreviewCarousel } from '../components/dashboard/QRPreviewCarousel';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'

interface DashboardStats {
  totalLinks: number
  totalScans: number
  totalBooks: number
  scansTrend: string
}

interface ActivityItem {
  id: string
  action: string
  time: string
  type: 'scan' | 'create' | 'book' | 'edit'
}

interface HealthStatus {
  overall: 'healthy' | 'warning' | 'error' | 'unknown'
  healthy: number
  warning: number
  error: number
  lastChecked?: Date
  isChecking: boolean
  results?: Array<{
    linkId: string
    slug: string
    title: string
    status: 'healthy' | 'warning' | 'error'
    errorMessage?: string
  }>
}

export default function DashboardOverview() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { subscription, checkQRCodeLimitAndRedirect, checkBookLimitAndRedirect, canCreateQRCode, canCreateBook, getQRUsageInfo, getBookUsageInfo, canAccessStatistics, canAccessReports, checkFeatureAccessAndRedirect } = usePlanLimits();
  const { needsPayment, trialStatus } = useTrial();
  const [stats, setStats] = useState<DashboardStats>({
    totalLinks: 0,
    totalScans: 0,
    totalBooks: 0,
    scansTrend: '+0%'
  });
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({
    overall: 'unknown',
    healthy: 0,
    warning: 0,
    error: 0,
    isChecking: false
  });

  // Check health status
  const checkHealth = async () => {
    if (healthStatus.isChecking || stats.totalLinks === 0) return;
    
    setHealthStatus(prev => ({ ...prev, isChecking: true }));
    
    try {
      const response = await fetch(`${API_BASE}/api/health-check/all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 429) {
        const data = await response.json();
        alert(`Bitte warten Sie noch ${data.retryAfter} Sekunden bis zur nächsten Prüfung`);
        return;
      }
      
      if (!response.ok) throw new Error('Health check failed');
      
      const data = await response.json();
      
      setHealthStatus({
        overall: data.overallStatus,
        healthy: data.healthyLinks,
        warning: data.warningLinks || 0,
        error: data.errorLinks,
        lastChecked: new Date(data.checkedAt),
        isChecking: false,
        results: data.results
      });
    } catch (error) {
      console.error('Health check error:', error);
      setHealthStatus(prev => ({ ...prev, isChecking: false }));
    }
  };

  // Load dashboard data from backend
  const loadDashboardData = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('📊 Loading dashboard data...');

      // Load QR links stats
      const linksResponse = await fetch(`${API_BASE}/api/links`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      let totalLinks = 0;
      let totalScans = 0;
      let linksData: any[] = [];

      if (linksResponse.ok) {
        const links = await linksResponse.json();
        linksData = links;
        totalLinks = links.length;
        totalScans = links.reduce((sum: number, link: any) => sum + (link.scans || 0), 0);
        
        // Check if any links have health status from previous checks
        if (totalLinks > 0) {
          const linksWithHealth = links.filter((link: any) => link.health_status && link.last_health_check);
          if (linksWithHealth.length > 0) {
            // Calculate health status from existing data
            const errorLinks = links.filter((link: any) => link.health_status === 'error');
            const warningLinks = links.filter((link: any) => link.health_status === 'warning');
            const healthyLinks = links.filter((link: any) => link.health_status === 'healthy');
            
            // Find the most recent check time
            const lastCheckTime = linksWithHealth
              .map((link: any) => new Date(link.last_health_check))
              .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];
            
            setHealthStatus({
              overall: errorLinks.length > 0 ? 'error' : warningLinks.length > 0 ? 'warning' : 'healthy',
              healthy: healthyLinks.length,
              warning: warningLinks.length,
              error: errorLinks.length,
              lastChecked: lastCheckTime,
              isChecking: false,
              results: links.map((link: any) => ({
                linkId: link.id,
                slug: link.slug,
                title: link.title,
                status: link.health_status || 'unknown',
                errorMessage: link.health_error
              }))
            });
          }
        }
      }

      // Try to load books stats (may not be implemented yet)
      let totalBooks = 0;
      try {
        const booksResponse = await fetch(`${API_BASE}/api/books`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (booksResponse.ok) {
          const books = await booksResponse.json();
          totalBooks = books.length;
        }
      } catch (err) {
        // Books API not implemented yet, ignore error
      }

      setStats({
        totalLinks,
        totalScans,
        totalBooks,
        scansTrend: totalScans > 0 ? '+12%' : '+0%' // Mock trend for now
      });

      // For now, we'll show a simple activity feed
      // In production, this would come from an activity log in the backend
      const activities: ActivityItem[] = [];
      
      if (totalLinks > 0) {
        activities.push({
          id: '1',
          action: `${totalLinks} QR-Code${totalLinks !== 1 ? 's' : ''} verwaltet`,
          time: 'heute',
          type: 'create'
        });
      }

      if (totalScans > 0) {
        activities.push({
          id: '2',
          action: `${totalScans} Gesamt-Scans erreicht`,
          time: 'aktuell',
          type: 'scan'
        });
      }

      if (totalBooks > 0) {
        activities.push({
          id: '3',
          action: `${totalBooks} Buch${totalBooks !== 1 ? '' : ''} hinzugefügt`,
          time: 'kürzlich',
          type: 'book'
        });
      }

      if (activities.length === 0) {
        activities.push({
          id: '0',
          action: 'Willkommen bei BeyondPages! Erstellen Sie Ihren ersten QR-Code.',
          time: 'jetzt',
          type: 'create'
        });
      }

      setRecentActivity(activities);
      console.log('✅ Dashboard data loaded');
    } catch (err) {
      console.error('❌ Failed to load dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [session]);

  const StatCard = ({ title, value, change, icon: Icon, color, onClick, className, tooltip }: any) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 ${className || ''}`}
      onClick={onClick}
      title={tooltip}
    >
      <div className="flex items-center">
        <div className={`p-1.5 rounded-lg ${color}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="ml-2 flex-1">
          <p className="text-xs font-medium text-gray-600">{title}</p>
          <div className="text-base font-bold text-gray-900">{value}</div>
        </div>
        {change && (
          <div className="text-xs text-green-600 font-medium">
            {change}
          </div>
        )}
      </div>
    </div>
  );

  const ActivityItem = ({ activity }: { activity: ActivityItem }) => {
    const getIcon = (type: string) => {
      switch (type) {
        case 'scan': return <MousePointer className="h-4 w-4 text-blue-500" />;
        case 'create': return <Plus className="h-4 w-4 text-green-500" />;
        case 'book': return <BookOpen className="h-4 w-4 text-purple-500" />;
        case 'edit': return <Activity className="h-4 w-4 text-orange-500" />;
        default: return <Activity className="h-4 w-4 text-gray-500" />;
      }
    };

    return (
      <div className="flex items-start space-x-3 py-3">
        <div className="flex-shrink-0">
          {getIcon(activity.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900">{activity.action}</p>
          <p className="text-xs text-gray-500">{activity.time}</p>
        </div>
      </div>
    );
  };

  const handleCreateQRCode = (e: React.MouseEvent) => {
    e.preventDefault();
    if (checkQRCodeLimitAndRedirect()) {
      navigate('/qr/dash/links?create=true');
    }
  };

  const handleCreateBook = (e: React.MouseEvent) => {
    e.preventDefault();
    if (checkBookLimitAndRedirect()) {
      navigate('/qr/dash/content?create=book');
    }
  };

  const handleCreateCampaign = (e: React.MouseEvent) => {
    e.preventDefault();
    if (checkFeatureAccessAndRedirect('campaigns')) {
      navigate('/qr/dash/campaigns?create=true');
    }
  };

  const handleInviteUser = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/qr/dash/team?invite=true');
  };

  const handleStatisticsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (checkFeatureAccessAndRedirect('statistics')) {
      navigate('/qr/dash/stats');
    }
  };

  const handleReportsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (checkFeatureAccessAndRedirect('reports')) {
      navigate('/qr/dash/reports');
    }
  };

  // Get usage info for visual indicators
  const qrUsageInfo = getQRUsageInfo();
  const bookUsageInfo = getBookUsageInfo();
  const isQRLimitReached = !canCreateQRCode();
  const isBookLimitReached = !canCreateBook();

  if (!user) {
    return <div>Nicht angemeldet</div>;
  }

  return (
    <>
      <TrialActivation />
      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Trial Banner */}
        <TrialBanner />
        
        {/* Trial Expired Warning */}
        {needsPayment && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">
                  Testphase abgelaufen - Eingeschränkte Funktionen
                </h3>
                <p className="text-sm text-red-800 mb-3">
                  Ihre 7-tägige Testphase ist abgelaufen. Sie können weiterhin Ihre bestehenden QR-Codes verwalten, 
                  aber der Zugriff auf erweiterte Funktionen ist eingeschränkt.
                </p>
                <div className="flex gap-3">
                  <Link
                    to="/qr/pricing"
                    className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                  >
                    Plan auswählen
                  </Link>
                  <Link
                    to="/qr/dash/profile"
                    className="inline-flex items-center px-3 py-1.5 bg-white text-red-700 text-sm font-medium rounded-md border border-red-300 hover:bg-red-50 transition-colors"
                  >
                    Konto verwalten
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-welcome">
          Willkommen zurück, {user.email?.split('@')[0] || 'Benutzer'}!
        </h1>
        <p className="text-gray-600 mt-1">
          Hier ist eine Übersicht Ihrer QR-Code-Aktivitäten
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700" data-testid="dashboard-error">
          ❌ {error}
          <button 
            onClick={loadDashboardData}
            className="ml-4 text-sm underline hover:no-underline"
            data-testid="dashboard-retry"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8" data-testid="dashboard-loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Dashboard wird geladen...</p>
        </div>
      )}

      {/* Stats Grid */}
      {!loading && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <StatCard
              title="QR-Codes"
              value={stats.totalLinks}
              icon={QrCode}
              color="bg-blue-500"
            />
            <StatCard
              title="Bücher"
              value={stats.totalBooks}
              icon={BookOpen}
              color="bg-green-500"
            />
            <StatCard
              title="Gesamt Scans"
              value={stats.totalScans.toLocaleString()}
              change={stats.scansTrend}
              icon={MousePointer}
              color="bg-purple-500"
            />
            <StatCard
              title="Letzter Check"
              value={
                stats.totalLinks === 0 ? (
                  "Keine QR-Codes"
                ) : healthStatus.lastChecked ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {healthStatus.overall === 'healthy' ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-500" />
                          <span className="text-lg">Alles OK</span>
                        </>
                      ) : healthStatus.overall === 'warning' ? (
                        <>
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                          <span className="text-lg">{healthStatus.warning} Warnung{healthStatus.warning !== 1 ? 'en' : ''}</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-5 w-5 text-red-500" />
                          <span className="text-lg">{healthStatus.error} Fehler</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(healthStatus.lastChecked).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })} um {new Date(healthStatus.lastChecked).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} Uhr
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-6 w-6 text-gray-400" />
                    <span>Nicht geprüft</span>
                  </div>
                )
              }
              icon={TrendingUp}
              color={
                stats.totalLinks === 0 ? 'bg-gray-400' :
                !healthStatus.lastChecked ? 'bg-gray-400' :
                healthStatus.overall === 'healthy' ? 'bg-green-500' :
                healthStatus.overall === 'warning' ? 'bg-yellow-500' :
                'bg-red-500'
              }
              onClick={() => {
                if (stats.totalLinks === 0) return;

                if ((healthStatus.overall === 'error' || healthStatus.overall === 'warning') && healthStatus.results) {
                  // Navigate to links page and show problematic links
                  const problemLinks = healthStatus.results.filter(r => r.status === 'error' || r.status === 'warning');
                  if (problemLinks.length > 0) {
                    navigate('/qr/dash/links?highlight=' + problemLinks[0].linkId);
                  }
                } else {
                  checkHealth();
                }
              }}
              className={stats.totalLinks > 0 ? "cursor-pointer hover:shadow-lg transition-shadow" : ""}
              tooltip={
                stats.totalLinks === 0 ? undefined :
                healthStatus.overall === 'error' ? "Klicken um fehlerhafte QR-Codes anzuzeigen" :
                healthStatus.overall === 'warning' ? "Klicken um QR-Codes mit Warnungen anzuzeigen" :
                "Klicken um alle QR-Codes zu prüfen"
              }
            />
          </div>

          {/* Health Status and Quick Actions in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
            {/* Health Status Details - Smaller compact version */}
            {stats.totalLinks > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                    {healthStatus.overall === 'healthy' ? (
                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    ) : healthStatus.overall === 'warning' ? (
                      <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                    ) : healthStatus.overall === 'error' ? (
                      <XCircle className="h-4 w-4 mr-2 text-red-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 mr-2 text-gray-400" />
                    )}
                    QR-Code Status
                  </h3>
                  <button
                    onClick={checkHealth}
                    disabled={healthStatus.isChecking}
                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center gap-1 disabled:opacity-50"
                    data-testid="health-check"
                  >
                    <RefreshCw className={`w-3 h-3 ${healthStatus.isChecking ? 'animate-spin' : ''}`} />
                    {healthStatus.isChecking ? 'Prüfe...' : 'Prüfen'}
                  </button>
                </div>
                
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="font-medium text-green-600">{healthStatus.healthy}</span>
                    <span className="text-gray-600">OK</span>
                  </div>
                  {healthStatus.warning > 0 && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium text-yellow-600">{healthStatus.warning}</span>
                      <span className="text-gray-600">Warnung{healthStatus.warning !== 1 ? 'en' : ''}</span>
                    </div>
                  )}
                  {healthStatus.error > 0 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span className="font-medium text-red-600">{healthStatus.error}</span>
                      <span className="text-gray-600">Fehler</span>
                    </div>
                  )}
                </div>
                
                {(healthStatus.error > 0 || healthStatus.warning > 0) && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <Link
                      to={`/qr/dash/links?filter=${healthStatus.error > 0 ? 'errors' : 'warnings'}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Probleme anzeigen →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Smaller Quick Actions Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Schnellaktionen</h3>
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={handleCreateQRCode}
                  className={`py-2 px-3 rounded-lg transition-colors flex flex-col items-center text-sm ${
                    isQRLimitReached 
                      ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                  }`}
                  title={isQRLimitReached ? `Plan-Limit erreicht (${qrUsageInfo?.used}/${qrUsageInfo?.limit})` : undefined}
                  data-testid="quick-create-qr"
                >
                  <QrCode className="h-4 w-4 mb-1" />
                  <span className="text-xs">Neuer QR</span>
                </button>
                
                <button
                  onClick={handleCreateBook}
                  className={`py-2 px-3 rounded-lg transition-colors flex flex-col items-center text-sm ${
                    isBookLimitReached 
                      ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                  }`}
                  title={isBookLimitReached ? `Plan-Limit erreicht (${bookUsageInfo?.used}/${bookUsageInfo?.limit})` : undefined}
                  data-testid="quick-create-book"
                >
                  <BookOpen className="h-4 w-4 mb-1" />
                  <span className="text-xs">Neues Buch</span>
                </button>
                
                <button
                  onClick={handleCreateCampaign}
                  className="py-2 px-3 rounded-lg transition-colors flex flex-col items-center text-sm bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
                  data-testid="quick-create-campaign"
                >
                  <Megaphone className="h-4 w-4 mb-1" />
                  <span className="text-xs">Kampagne</span>
                </button>
                
                <button
                  onClick={handleInviteUser}
                  className="py-2 px-3 rounded-lg transition-colors flex flex-col items-center text-sm bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200"
                  data-testid="quick-invite-user"
                >
                  <UserPlus className="h-4 w-4 mb-1" />
                  <span className="text-xs">Einladen</span>
                </button>
              </div>
            </div>
          </div>

          {/* Book Carousel */}
          <div className="mb-4">
            <BookCarousel />
          </div>

          {/* QR Preview Carousel */}
          <div className="mb-4">
            <QRPreviewCarousel />
          </div>

          {/* Getting Started */}
          {stats.totalLinks === 0 && (
            <div className="mt-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center">
                <QrCode className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Ersten QR-Code erstellen</h3>
                  <p className="mt-1 opacity-90">
                    Beginnen Sie mit der Erstellung Ihres ersten QR-Codes für Spotify, YouTube, PDFs oder jede andere URL.
                  </p>
                  <button
                    onClick={handleCreateQRCode}
                    className={`mt-3 inline-flex items-center px-4 py-2 rounded-lg transition-colors font-medium ${
                      isQRLimitReached 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-white text-blue-600 hover:bg-gray-100'
                    }`}
                    title={isQRLimitReached ? `Plan-Limit erreicht (${qrUsageInfo?.used}/${qrUsageInfo?.limit})` : undefined}
                  >
                    {isQRLimitReached ? 'Upgrade für QR-Codes' : 'Jetzt starten'}
                    <QrCode className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
        </div>
      </div>
    </>
  );
} 
